import { assign, fromPromise, setup } from 'xstate';

import { appEvents } from '@/services/appEvents';

import type { AppEvent, MapView, TourId } from '@/services/appEvents';

export const RESTORATION_TOUR_ID: TourId = 'restoration';

/** The tour's own buttons; everything else it hears is an app event. */
export type TourCommand = { type: 'tour.next' } | { type: 'tour.back' } | { type: 'tour.skip' };

export type TourMachineEvent = AppEvent | TourCommand;

/** Steps in the order they appear; every one moves on with Next and back with Back. */
export const RESTORATION_TOUR_STEPS = [
  'intro',
  'glance',
  'add_area',
  'read_result',
  'predictor',
  'take_further',
] as const;

export type RestorationTourStep = (typeof RESTORATION_TOUR_STEPS)[number];

export interface TourEligibility {
  isFreeTier: boolean;
  hasMangroveSuitability: boolean;
  /** Finished or skipped before. */
  alreadyDone: boolean;
}

export interface TourInput {
  view: MapView;
}

interface TourContext {
  /** Tracked until the tour starts. */
  view: MapView;
  onboardingResolved: boolean;
  step: RestorationTourStep | null;
}

const enter = (step: RestorationTourStep) => ({ type: 'enterStep' as const, params: { step } });

/** Move on from `from`, announcing it done. */
const forward = (from: RestorationTourStep, target: string) => ({
  target,
  actions: { type: 'announceStepCompleted' as const, params: { step: from } },
});

/**
 * The restoration planner's onboarding tour.
 *
 * - `gate`: load eligibility, then wait for onboarding to resolve and for the Maps view.
 * - `touring`: click-through steps. The last three run inside the demo area, which the
 *   tour asks the map to open on the way in and close on the way out.
 * - `completed` / `skipped` / `ineligible`: resting states; `help.tour_requested`
 *   restarts from the intro.
 *
 * Moves only on the events it is sent; never touches stores or the DOM.
 */
export const restorationTourMachine = setup({
  types: {
    context: {} as TourContext,
    events: {} as TourMachineEvent,
    input: {} as TourInput,
  },
  actors: {
    loadEligibility: fromPromise<TourEligibility>(() => {
      throw new Error('loadEligibility must be provided');
    }),
  },
  actions: {
    enterStep: assign((_, params: { step: RestorationTourStep }) => ({ step: params.step })),
    trackView: assign({
      view: ({ context, event }) =>
        event.type === 'view.changed' ? event.payload.to : context.view,
    }),
    noteOnboardingResolved: assign({ onboardingResolved: true }),
    announceStarted: () => appEvents.emit('tour.started', { tour: RESTORATION_TOUR_ID }),
    announceStepCompleted: (_, params: { step: RestorationTourStep }) =>
      appEvents.emit('tour.step_completed', { tour: RESTORATION_TOUR_ID, step: params.step }),
    announceSkipped: ({ context }) =>
      appEvents.emit('tour.skipped', {
        tour: RESTORATION_TOUR_ID,
        step: context.step ?? 'intro',
      }),
    announceCompleted: () => appEvents.emit('tour.completed', { tour: RESTORATION_TOUR_ID }),
    markDone: () => {},
    openDemoArea: () => appEvents.emit('tour.demo_area_requested'),
    closeDemoArea: () => appEvents.emit('tour.demo_area_closed'),
  },
  guards: {
    isEligible: (_, eligibility: TourEligibility) =>
      eligibility.isFreeTier && eligibility.hasMangroveSuitability && !eligibility.alreadyDone,
    isReadyToStart: ({ context }) => context.onboardingResolved && context.view === 'maps',
    // The menu only offers the tour to users who can take it.
    isReplayRequest: ({ event }) =>
      event.type === 'help.tour_requested' && event.payload.tour === RESTORATION_TOUR_ID,
  },
}).createMachine({
  id: 'restorationTour',
  context: ({ input }) => ({
    view: input.view,
    onboardingResolved: false,
    step: null,
  }),
  initial: 'gate',
  on: {
    'help.tour_requested': {
      guard: 'isReplayRequest',
      target: '.touring',
      reenter: true,
    },
  },
  states: {
    gate: {
      initial: 'checking',
      on: {
        'view.changed': { actions: 'trackView' },
        'onboarding.resolved': { actions: 'noteOnboardingResolved' },
      },
      states: {
        checking: {
          invoke: {
            src: 'loadEligibility',
            onDone: [
              {
                guard: { type: 'isEligible', params: ({ event }) => event.output },
                target: 'waiting',
              },
              { target: '#restorationTour.ineligible' },
            ],
            onError: 'unavailable',
          },
        },
        // A failed check is asked again on the next view change, rather than ruling the tour
        // out until the page reloads.
        unavailable: {
          on: { 'view.changed': { target: 'checking', actions: 'trackView' } },
        },
        waiting: {
          always: { guard: 'isReadyToStart', target: '#restorationTour.touring' },
        },
      },
    },

    touring: {
      entry: 'announceStarted',
      initial: 'intro',
      on: {
        'tour.skip': { target: 'skipped', actions: 'announceSkipped' },
      },
      states: {
        intro: {
          entry: enter('intro'),
          on: { 'tour.next': forward('intro', 'glance') },
        },
        glance: {
          entry: enter('glance'),
          on: { 'tour.next': forward('glance', 'add_area') },
        },
        add_area: {
          entry: enter('add_area'),
          on: {
            'tour.next': forward('add_area', 'demo_area'),
            'tour.back': 'glance',
          },
        },
        // Opened on the way in and closed on the way out, by Back, Skip or Finish alike, so the
        // tour always ends on the project's overview.
        demo_area: {
          entry: 'openDemoArea',
          exit: 'closeDemoArea',
          initial: 'read_result',
          states: {
            read_result: {
              entry: enter('read_result'),
              on: {
                'tour.next': forward('read_result', 'predictor'),
                'tour.back': '#restorationTour.touring.add_area',
              },
            },
            predictor: {
              entry: enter('predictor'),
              on: {
                'tour.next': forward('predictor', 'take_further'),
                'tour.back': 'read_result',
              },
            },
            take_further: {
              entry: enter('take_further'),
              on: {
                'tour.next': forward('take_further', '#restorationTour.completed'),
                'tour.back': 'predictor',
              },
            },
          },
        },
      },
    },

    completed: {
      entry: ['announceCompleted', 'markDone'],
    },
    skipped: {
      entry: 'markDone',
    },
    ineligible: {},
  },
});
